'use client';

import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import {
  type Country,
  getCountryCallingCode,
  getCountries,
} from 'react-phone-number-input/min';
import flags from 'react-phone-number-input/flags';
import PhoneInput, {
  type PhoneInputProps,
  type Country as RPNICountry,
} from 'react-phone-number-input';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input, type InputProps } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';


interface CountrySelectProps {
  disabled?: boolean;
  value: Country;
  onChange: (value: Country) => void;
  options: { label: string; value: RPNICountry }[];
}

const CountrySelect = ({
  disabled,
  value,
  onChange,
  options,
}: CountrySelectProps) => {
  const handleSelect = React.useCallback(
    (country: RPNICountry) => {
      onChange(country);
    },
    [onChange]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={'outline'}
          className={cn('flex gap-1 rounded-e-none rounded-s-lg pl-3 pr-1')}
          disabled={disabled}
        >
          <span className="flex items-center truncate">
            <div className="flex items-center">
              {flags[value] && React.createElement(flags[value], { title: value, className: "w-4 h-4" })}
            </div>
          </span>
          <ChevronsUpDown
            className={cn(
              '-mr-2 h-4 w-4 opacity-50',
              disabled ? 'hidden' : 'opacity-100'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandList>
            <ScrollArea className="h-72">
              <CommandInput placeholder="Search country..." />
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {options
                  .filter((x) => x.value)
                  .map((option) => (
                    <CommandItem
                      className="gap-2"
                      key={option.value}
                      onSelect={() => handleSelect(option.value)}
                    >
                      {flags[option.value] && React.createElement(flags[option.value], { title: option.value, className: "w-4 h-4" })}
                      <span className="flex-1">{option.label}</span>
                      <span className="text-sm text-foreground/50">
                        {`+${getCountryCallingCode(option.value)}`}
                      </span>
                      <Check
                        className={cn(
                          'ml-auto h-4 w-4',
                          value === option.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};


const PhoneInputComponent = React.forwardRef<
  React.ElementRef<typeof PhoneInput>,
  Omit<React.ComponentPropsWithoutRef<typeof PhoneInput>, 'onChange'> & {
    onChange: (value: string) => void;
  } & InputProps
>(({ className, onChange, ...props }, ref) => {
  return (
    <PhoneInput
      ref={ref}
      className={cn('flex', className)}
      onChange={onChange}
      inputComponent={Input}
      countrySelectComponent={CountrySelect}
      countryOptionsOrder={getCountries()}
      {...props}
    />
  );
});
PhoneInputComponent.displayName = 'PhoneInput';

export { PhoneInputComponent as PhoneNumberInput };
